/**
 * Enhanced mega menu — add-on script (new file; no Horizon file is edited).
 *
 * How it plugs in without touching Horizon:
 *   1. sections/mega-menu-enhanced.liquid (a section in the header group)
 *      holds the panel markup for every top-level menu link.
 *   2. Once Horizon has finished its own header hydration, this script
 *      attaches each panel inside Horizon's own submenu / mobile drawer
 *      container for the same link. Horizon keeps doing the open/close,
 *      hover-bridge, sizing and animation; this only supplies the contents.
 *   3. Anything going wrong (fetch failure, menu mismatch, no match for a
 *      link) leaves Horizon's stock menu in place — data-nm-pending is always
 *      cleared, and stock content is only ever hidden by CSS, never removed.
 */
(() => {
  'use strict';

  if (window.__nmMegaMenu) {
    window.__nmMegaMenu.init();
    return;
  }

  /**
   * Grace period before hovering a different row actually switches it in.
   * Moving the mouse down toward a lower row necessarily passes over every
   * row in between — without this, each one it merely passes through would
   * instantly collapse whatever was open and expand itself, reflowing the
   * list. Only a row the pointer settles on for longer than this commits.
   */
  const NM_SWITCH_DELAY_MS = 200;

  /** Give up waiting for Horizon's header hydration after this long. */
  const HYDRATION_WAIT_MS = 10000;

  /* ------------------------------------------------------------------ *
   * Desktop panel behaviour (row switching, editorial image, sizing)
   * ------------------------------------------------------------------ */
  class NMPanel {
    constructor(el) {
      this.el = el;
      this.navItems = Array.from(el.querySelectorAll('[data-nm-nav-item]'));
      this.productSets = Array.from(el.querySelectorAll('[data-nm-set]'));
      this.editorials = Array.from(el.querySelectorAll('[data-nm-editorial]'));
      this.activeTarget = null;
      this._lastPointer = null;
      this._pendingItem = null;
      this._pendingTimer = undefined;

      // Horizon opens a submenu by setting data-active and clearing `inert`
      // (and closes by reversing both). Items inside the "More" overflow list
      // only get the `inert` change, so watch both. Sizing and "open the
      // first row" key off this.
      this.submenu = el.closest('.menu-list__submenu');

      // Applies to every category, even a pure tile-grid "category directory"
      // (e.g. "Shop by concern") that has no nav rows of its own.
      this._bindFitHeight();

      if (!this.navItems.length) return;

      this._bindNavItems();
      this._observeSubmenuOpen();
      this._bindPointerReconciliation();
    }

    _isOpen() {
      return !!this.submenu && (this.submenu.hasAttribute('data-active') || !this.submenu.inert);
    }

    /**
     * Collapsing the previously-active row (to expand this one) shrinks its
     * height, which can shift rows right out from under a pointer that hasn't
     * moved. Track the last real pointer position so `_activate` can re-check
     * what's underneath it once the reflow has landed.
     */
    _bindPointerReconciliation() {
      this.el.addEventListener('mousemove', (e) => {
        this._lastPointer = { x: e.clientX, y: e.clientY };
      });
    }

    _reconcilePointerTarget() {
      if (!this._lastPointer) return;
      const { x, y } = this._lastPointer;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const hit = document.elementFromPoint(x, y);
          const navItem = hit?.closest ? hit.closest('[data-nm-nav-item]') : null;
          if (!navItem || !this.navItems.includes(navItem)) return;
          if ((navItem.dataset.nmTarget || 'default') !== this.activeTarget) {
            this._activate(navItem);
          }
        });
      });
    }

    /**
     * Make the panel fill (not just cap at) whatever space is available below
     * it, so short curated menus still use the full drop height. A CSS
     * vh-based calc can't account for variable chrome above the header
     * (announcement bars, review bars), so this measures the real position.
     *
     * Horizon's header-menu.js measures the submenu once when an item is
     * hovered and bakes that into --submenu-height / --full-open-header-height
     * (which drive the reveal window). We correct those same two properties
     * right after, exactly as Horizon would have written them had it measured
     * after us. Horizon's own deferred-content observer also re-measures for a
     * short window after activation; the values agree.
     */
    fit() {
      const headerComponent = this.el.closest('header-component');
      const top = this.el.getBoundingClientRect().top;
      const available = Math.max(300, Math.floor(window.innerHeight - top - 16));
      this.el.style.height = available + 'px';
      this.el.style.maxHeight = available + 'px';

      if (headerComponent) {
        const submenuHeight = this.el.offsetHeight;
        const headerVisibleHeight = headerComponent.offsetHeight;
        headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
        headerComponent.style.setProperty('--full-open-header-height', `${submenuHeight + headerVisibleHeight}px`);
      }
    }

    _bindFitHeight() {
      this.fit();
      window.addEventListener('resize', () => this.fit());

      if (!this.submenu) return;
      new MutationObserver(() => {
        if (this._isOpen()) {
          // Let Horizon's own synchronous measurement land first, then correct
          // it before the next paint so there's no visible flash.
          requestAnimationFrame(() => this.fit());
        }
      }).observe(this.submenu, { attributes: true, attributeFilter: ['data-active', 'inert'] });
    }

    _bindNavItems() {
      this.navItems.forEach((item) => {
        // Plain rows (e.g. a glossary page link) have no product set or tile
        // grid of their own — nothing to show in place of whatever's already
        // open. Passing over one should never collapse the active row; only
        // rows that actually have something to switch to can trigger a switch.
        if (item.dataset.nmTarget) {
          item.addEventListener('mouseenter', () => this._scheduleActivate(item));
          item.addEventListener('mouseleave', () => this._cancelPendingActivate(item));
          // Keyboard focus has no "just passing through" equivalent.
          item.addEventListener('focusin', () => this._activate(item));
        }

        item.addEventListener('keydown', (e) => {
          const idx = this.navItems.indexOf(item);
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.navItems[Math.min(idx + 1, this.navItems.length - 1)]?.focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.navItems[Math.max(idx - 1, 0)]?.focus();
          }
        });
      });
    }

    _observeSubmenuOpen() {
      if (!this.submenu) return;

      let wasOpen = this._isOpen();
      if (wasOpen) this._activateFirst();

      new MutationObserver(() => {
        const open = this._isOpen();
        if (open && !wasOpen) {
          wasOpen = true;
          this._activateFirst();
        } else if (!open) {
          wasOpen = false;
          this.activeTarget = null;
        }
      }).observe(this.submenu, { attributes: true, attributeFilter: ['data-active', 'inert'] });
    }

    _activateFirst() {
      if (this.navItems[0]) this._activate(this.navItems[0]);
    }

    /**
     * (Re)start the settle-and-switch delay for `item`. Cancelled by
     * `_cancelPendingActivate` if the pointer leaves it again before this
     * fires — i.e. it was just passing through on the way to somewhere else.
     */
    _scheduleActivate(item) {
      const target = item.dataset.nmTarget || 'default';
      if (target === this.activeTarget) return;
      if (this._pendingItem === item) return;

      clearTimeout(this._pendingTimer);
      this._pendingItem = item;
      this._pendingTimer = setTimeout(() => {
        this._pendingItem = null;
        this._pendingTimer = undefined;
        this._activate(item);
      }, NM_SWITCH_DELAY_MS);
    }

    _cancelPendingActivate(item) {
      if (this._pendingItem === item) {
        clearTimeout(this._pendingTimer);
        this._pendingItem = null;
        this._pendingTimer = undefined;
      }
    }

    _activate(item) {
      const target = item.dataset.nmTarget || 'default';
      if (target === this.activeTarget) return;
      this.activeTarget = target;

      const pinnedItem = this.navItems[0];
      this.navItems.forEach((el) => {
        const active = el === item || el === pinnedItem;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this._showProductSet(target);
      this._showEditorial(target);
      this._emitAnalytics(item);
      this._reconcilePointerTarget();
    }

    _showProductSet(target) {
      const def = this.el.querySelector('[data-nm-set="default"]');
      let collectionShown = false;
      // The first row (Best Sellers, or a "Shop by X" tile group) stays open
      // no matter what else is hovered; other rows open in addition to it.
      const pinnedTarget = this.navItems[0]?.dataset.nmTarget || null;

      this.productSets.forEach((set) => {
        if (set.dataset.nmSet === 'default') return;
        if (set.dataset.nmSet === target || set.dataset.nmSet === pinnedTarget) {
          set.hidden = false;
          collectionShown = true;
        } else {
          set.hidden = true;
        }
      });

      if (def) def.hidden = collectionShown;
    }

    _showEditorial(target) {
      const fallback = this.el.querySelector('[data-nm-editorial="fallback"]');
      let collectionShown = false;

      this.editorials.forEach((ed) => {
        if (ed.dataset.nmEditorial === 'fallback') return;
        if (ed.dataset.nmEditorial === target) {
          this._fadeIn(ed);
          collectionShown = true;
        } else {
          this._fadeOut(ed);
        }
      });

      if (fallback) {
        if (collectionShown) {
          this._fadeOut(fallback);
        } else {
          this._fadeIn(fallback);
        }
      }
    }

    _fadeIn(el) {
      el.hidden = false;
      requestAnimationFrame(() => el.classList.add('is-active'));
    }

    _fadeOut(el) {
      el.classList.remove('is-active');
      const DURATION = 230;
      setTimeout(() => {
        if (!el.classList.contains('is-active')) el.hidden = true;
      }, DURATION);
    }

    _emitAnalytics(item) {
      document.dispatchEvent(
        new CustomEvent('nm_panel_interaction', {
          bubbles: true,
          detail: {
            label: item.querySelector('.nm-panel__nav-label')?.textContent?.trim(),
            href: item.getAttribute('href'),
            target: item.dataset.nmTarget || null,
          },
        })
      );
    }
  }

  /* ------------------------------------------------------------------ *
   * Mobile accordion (tap to expand rows inside the drawer)
   * ------------------------------------------------------------------ */
  function initAccordion(accordion) {
    const rows = Array.from(accordion.querySelectorAll('[data-nm-accordion-trigger]'));
    // The row that opens expanded by default (the tile group, or Best Sellers)
    // stays open while other rows are opened and closed, but it can still be
    // collapsed (and re-opened) by tapping its own heading.
    const pinned = rows.find((row) => row.getAttribute('aria-expanded') === 'true') || null;

    rows.forEach((trigger) => {
      const panel = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!panel) return;

      trigger.addEventListener('click', () => {
        const isOpen = trigger.getAttribute('aria-expanded') === 'true';

        if (trigger !== pinned) {
          rows.forEach((other) => {
            if (other === trigger || other === pinned) return;
            other.setAttribute('aria-expanded', 'false');
            const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
            if (otherPanel) otherPanel.hidden = true;
          });
        }

        trigger.setAttribute('aria-expanded', String(!isOpen));
        panel.hidden = isOpen;
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Attaching panels into Horizon's own containers
   * ------------------------------------------------------------------ */
  const attachedDesktop = new WeakSet();

  function mountDesktop(sourceHost) {
    const index = sourceHost.dataset.nmDesktop;
    const inner = document.getElementById(`submenu-${index}`);
    if (!inner) return false;

    // The same menu must be driving both: confirm this submenu belongs to a
    // link with the URL the panel was built for.
    const wrapper = inner.closest('.menu-list__list-item');
    const stockLink = wrapper?.querySelector('a.menu-list__link');
    const expectedUrl = sourceHost.dataset.nmUrl;
    if (stockLink && expectedUrl && stockLink.getAttribute('href') !== expectedUrl) return false;

    const existing = inner.querySelector(':scope > .nm-panel');
    if (existing) return attachedDesktop.has(existing) || (attachedDesktop.add(existing), new NMPanel(existing), true);

    const source = sourceHost.querySelector('.nm-panel');
    if (!source) return false;

    const panel = document.importNode(source, true);
    inner.appendChild(panel);
    attachedDesktop.add(panel);
    new NMPanel(panel);
    return true;
  }

  function mountMobile(sourceHost) {
    const handle = sourceHost.dataset.nmMobile;
    const submenu = document.getElementById(`link-${handle}`);
    const inner = submenu?.querySelector('.menu-drawer__inner-submenu');
    if (!inner) return false;
    if (inner.querySelector(':scope > .nm-mobile')) return true;

    const block = document.importNode(sourceHost, true);
    block.removeAttribute('data-nm-mobile');
    block.setAttribute('data-nm-mobile-mounted', handle);

    const buttons = inner.querySelector(':scope > .menu-drawer__nav-buttons');
    if (buttons) {
      buttons.after(block);
    } else {
      inner.prepend(block);
    }
    block.querySelectorAll('[data-nm-accordion]').forEach(initAccordion);
    return true;
  }

  /* ------------------------------------------------------------------ *
   * Orchestration
   * ------------------------------------------------------------------ */
  function headerSection() {
    return document.querySelector('header-component')?.closest('.shopify-section') ?? null;
  }

  /**
   * Horizon re-renders the header's menu + drawer once after load (section
   * hydration) and overwrites anything we attached before it finishes. Wait for
   * its data-hydrated flag. It never hydrates in the theme editor.
   */
  function whenHeaderReady(root) {
    return new Promise((resolve) => {
      const section = headerSection();
      if (!section || root.dataset.designMode === 'true' || section.dataset.hydrated === 'true') {
        resolve();
        return;
      }
      let timer;
      const observer = new MutationObserver(() => {
        if (section.dataset.hydrated === 'true') finish();
      });
      const finish = () => {
        observer.disconnect();
        clearTimeout(timer);
        resolve();
      };
      observer.observe(section, { attributes: true, attributeFilter: ['data-hydrated'] });
      timer = setTimeout(finish, HYDRATION_WAIT_MS);
    });
  }

  async function loadSource(root) {
    // Theme editor / eager render: the markup is already in the page.
    if (root.dataset.eager === 'true') return root;

    const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(root.dataset.sectionId)}`);
    if (!response.ok) throw new Error(`mega menu source ${response.status}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const source = doc.querySelector('[data-nm-root]');
    if (!source) throw new Error('mega menu source missing');
    return source;
  }

  function clearPending() {
    document.documentElement.removeAttribute('data-nm-pending');
  }

  let running = false;

  async function init() {
    const root = document.querySelector('[data-nm-root]');
    if (!root) {
      clearPending();
      return;
    }
    if (running) return;
    running = true;

    try {
      await whenHeaderReady(root);
      const source = await loadSource(root);

      let attached = 0;
      source.querySelectorAll('[data-nm-desktop]').forEach((host) => {
        if (mountDesktop(host)) attached += 1;
      });
      source.querySelectorAll('[data-nm-mobile]').forEach((host) => {
        if (mountMobile(host)) attached += 1;
      });

      if (!attached) console.warn('[mega menu] no panels attached — check the section uses the same menu as the header.');
    } catch (error) {
      console.warn('[mega menu] falling back to the default menu:', error);
    } finally {
      clearPending();
      running = false;
    }
  }

  window.__nmMegaMenu = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: the header or this section re-renders on every edit.
  document.addEventListener('shopify:section:load', init);
})();
